import { useRef, useState } from 'react';
import { Upload, FileText, Loader2, CheckCircle2, XCircle, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { parseDjiLog } from '../../lib/djiLogParser';
import { addFlight, pruneOldSynced } from '../../lib/flightSyncDb';
import { flushQueue } from '../../lib/flightSyncApi';
import FlightRecordHints from './FlightRecordHints';

export default function ImportPage({ ctx }) {
  const [drag, setDrag] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setParsing(true);
    try {
      let ok = 0;
      let fail = 0;
      for (const file of files) {
        try {
          const parsed = await parseDjiLog(file);
          const flight = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            ...parsed,
            pilot_name: ctx.pilot_name,
            drone_model: parsed.drone_model || ctx.drone_model,
            drone_serial: parsed.drone_serial || ctx.drone_serial,
            synced: false,
            created_at: new Date().toISOString(),
          };
          await addFlight(flight);
          setLastResult({ success: true, flight });
          ok++;
        } catch (e) {
          setLastResult({ success: false, error: e.message, file: file.name });
          fail++;
        }
      }
      if (ok > 0) {
        toast.success(`${ok} voo(s) importado(s). Iniciando sincronizacao...`);
        await pruneOldSynced();
        if (navigator.onLine) flushQueue(ctx.token);
      }
      if (fail > 0) toast.error(`${fail} arquivo(s) com erro`);
    } finally {
      setParsing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid="fs-import-page">
      <div className="fs-hero">
        <div className="fs-hero-label">IMPORTAR LOG DE VOO</div>
        <div className="fs-hero-value">Arquivos DJI</div>
        <div className="fs-hero-sub">Suporta .csv, .txt (DJI Fly / GO 4) e .dat (passthrough - arquivo armazenado para processamento no servidor).</div>
      </div>

      <div className="fs-card">
        <div className="fs-card-title"><Upload size={12} /> ADICIONAR ARQUIVOS</div>
        <div
          className={`fs-dropzone${drag ? ' drag' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(Array.from(e.dataTransfer.files)); }}
          onClick={() => inputRef.current?.click()}
          data-testid="fs-dropzone"
        >
          <div className="fs-dropzone-icon"><Cloud size={24} style={{ color: 'var(--fs-cyan)' }} /></div>
          {parsing ? (
            <div style={{ color: 'var(--fs-cyan)', fontWeight: 600 }}>
              <Loader2 size={16} className="fs-spin" style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Processando arquivo...
            </div>
          ) : (
            <>
              <div style={{ color: 'var(--fs-text)', fontWeight: 600, marginBottom: 4 }}>
                Arraste arquivos ou clique para selecionar
              </div>
              <div style={{ fontSize: 12, color: 'var(--fs-text-dim)' }}>
                .csv / .txt / .dat | multiplos arquivos OK
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".csv,.txt,.dat"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid="fs-file-input"
          />
        </div>
      </div>

      {lastResult && lastResult.success && (
        <div className="fs-card" data-testid="fs-last-result">
          <div className="fs-card-title" style={{ color: 'var(--fs-green)' }}>
            <CheckCircle2 size={14} /> ULTIMO VOO IMPORTADO
          </div>
          <FlightSummary f={lastResult.flight} />
        </div>
      )}

      {lastResult && !lastResult.success && (
        <div className="fs-card" style={{ borderColor: 'rgba(239,68,68,0.35)' }}>
          <div className="fs-card-title" style={{ color: 'var(--fs-red)' }}><XCircle size={14} /> ERRO AO PROCESSAR</div>
          <p style={{ margin: 0, fontSize: 13 }}>
            <strong>{lastResult.file}</strong>: {lastResult.error}
          </p>
        </div>
      )}

      <div className="fs-card">
        <div className="fs-card-title"><FileText size={12} /> DICAS DE EXPORTACAO</div>
        <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--fs-text-dim)', fontSize: 13, lineHeight: 1.7 }}>
          <li>No <strong>DJI Fly</strong>: Perfil {'>'} Registro de voo {'>'} Exportar .csv</li>
          <li>No <strong>DJI Pilot 2</strong>: Documentos {'>'} Registros de voo {'>'} Compartilhar</li>
          <li>No <strong>DJI GO 4</strong>: Abra em <em>phantomhelp.com</em> ou <em>airdata.com</em> e exporte .csv</li>
          <li>Transfira para este dispositivo via USB, AirDrop ou nuvem</li>
        </ol>
      </div>

      {/* FlightRecord folder hints - helps pilot find logs and activate auto-sync */}
      <FlightRecordHints />
    </div>
  );
}

function FlightSummary({ f }) {
  return (
    <div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Drone</span>
        <span className="fs-metric-value">{f.drone_model}</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Duracao</span>
        <span className="fs-metric-value">{secToMin(f.duration_seconds)}</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Distancia</span>
        <span className="fs-metric-value">{(f.distance_meters / 1000).toFixed(2)} km</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Altitude maxima</span>
        <span className="fs-metric-value">{(f.max_altitude_meters || 0).toFixed(1)} m</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Velocidade maxima</span>
        <span className="fs-metric-value">{(f.max_speed_ms || 0).toFixed(1)} m/s</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Pontos GPS</span>
        <span className="fs-metric-value">{f.gps_track?.length || 0}</span>
      </div>
      <div className="fs-metric-row">
        <span className="fs-metric-label">Bateria</span>
        <span className="fs-metric-value">{f.battery_start_pct ?? '-'}% → {f.battery_end_pct ?? '-'}%</span>
      </div>
    </div>
  );
}

function secToMin(s) {
  if (!s) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}
